import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  getCurrentSubscription,
  isIOSPWARequired,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/lib/pushNotifications";

/**
 * Card "Notifiche Push" per la pagina Profilo.
 * Feature-detect: se non supportato, mostra messaggio; se iOS non-standalone, richiede install PWA.
 */
export default function PushNotificationsCard() {
  const supported = isPushSupported();
  const iosNeedsPwa = isIOSPWARequired();
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  useEffect(() => {
    if (!supported) return;
    let mounted = true;
    (async () => {
      const s = await getCurrentSubscription();
      if (mounted) setSubscribed(!!s);
    })();
    return () => { mounted = false; };
  }, [supported]);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission("granted");
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (e) {
      toast.error(e?.message || "Attivazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Notifiche push disattivate");
    } catch (e) {
      toast.error(e?.message || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const doSelfTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test-self");
      if ((data.sent || 0) > 0 && (data.failed || 0) === 0) {
        toast.success("Notifica inviata — dovresti vederla tra pochi secondi");
      } else if ((data.sent || 0) > 0) {
        toast.warning("Notifica inviata ma potrebbe non essere ricevuta — verifica i permessi del browser");
      } else {
        toast.error("Errore nell'invio. Riprova.");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Nessun dispositivo iscritto. Attiva le notifiche prima.");
      } else if (status === 429) {
        toast.error("Troppi test push. Riprova tra un minuto.");
      } else {
        toast.error("Errore nell'invio. Riprova.");
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="ih-glass rounded-2xl p-6" data-testid="push-notifications-card">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="h-4 w-4 text-lavender-500" />
        <h2 className="font-display text-lg tracking-tight">Notifiche Push</h2>
      </div>

      {!supported ? (
        <div className="rounded-xl border border-border/60 bg-background/40 p-4 text-sm text-muted-foreground flex items-start gap-2">
          <Smartphone className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Il tuo browser non supporta le notifiche push. Aggiorna a una versione recente di Chrome, Edge, Firefox o Safari 16.4+.</span>
        </div>
      ) : iosNeedsPwa ? (
        <div className="rounded-xl border border-amber-200/60 bg-amber-50/40 dark:bg-amber-950/20 p-4 text-sm space-y-2">
          <div className="font-medium flex items-center gap-1.5"><Send className="h-4 w-4 text-amber-600" /> Su iOS installa la PWA</div>
          <p className="text-xs text-muted-foreground leading-snug">
            Le notifiche push su iPhone/iPad funzionano solo dopo aver installato IH AuditFlow sulla home:
            apri il menu <strong>Condividi</strong> di Safari e scegli <strong>Aggiungi a Home</strong>.
            Poi rientra dall'icona sulla home e attiva le notifiche da qui.
          </p>
        </div>
      ) : permission === "denied" ? (
        <div className="rounded-xl border border-rose-200/60 bg-rose-50/40 dark:bg-rose-950/20 p-4 text-sm">
          Le notifiche sono state <strong>bloccate</strong> per questo sito. Sbloccale dalle impostazioni del browser e ricarica la pagina.
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="font-medium flex items-center gap-2">
              {subscribed ? (<><Bell className="h-4 w-4 text-emerald-500" /> Attive</>) : (<><BellOff className="h-4 w-4 text-muted-foreground" /> Non attive</>)}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-snug max-w-md">
              Ricevi un avviso in tempo reale quando ti viene assegnata una task, quando c'è un alert critico
              o quando una scadenza si avvicina.
            </p>
          </div>
          {subscribed ? (
            <Button data-testid="push-unsubscribe-btn" variant="outline" onClick={doUnsubscribe} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />} Disattiva
            </Button>
          ) : (
            <Button data-testid="push-subscribe-btn" onClick={doSubscribe} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Attiva notifiche
            </Button>
          )}
        </div>
      )}

      {supported && !iosNeedsPwa && permission !== "denied" && subscribed && (
        <div className="mt-4 pt-4 border-t border-border/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-muted-foreground max-w-md">
            Utile per verificare che il tuo browser/dispositivo riceva correttamente le notifiche.
          </p>
          <Button
            data-testid="push-self-test-btn"
            variant="secondary"
            onClick={doSelfTest}
            disabled={testing || busy}
            className="gap-2"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Inviami una notifica di prova
          </Button>
        </div>
      )}
    </div>
  );
}
